import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Star, Trash2, ChevronDown } from "lucide-react";

const FAV_KEYS = ["medication", "catalog_id", "presentation_id", "strength", "form", "route", "frequency", "directions",
  "quantity", "quantity_unit", "refills", "days_supply", "max_daily_dose"];

function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return { open, setOpen, ref };
}

const favLabel = (f) => [f.medication, f.strength, f.form].filter(Boolean).join(" ");

function FavRow({ f, canDelete, onPick, onDelete, testId }) {
  return (
    <div className="flex items-center gap-1 hover:bg-[#F5F2EB]">
      <button type="button" data-testid={`${testId}-option-${f.id}`} onClick={() => onPick(f)} className="min-w-0 flex-1 px-3 py-2 text-left">
        <span className="block truncate text-sm font-semibold text-[#3E2723]">{favLabel(f)}</span>
        <span className="block truncate text-[11px] text-[#7B6157]">{[f.directions || f.frequency, f.quantity && `Qty ${f.quantity}`, f.refills && `Refills ${f.refills}`].filter(Boolean).join(" · ")}</span>
      </button>
      {canDelete && <button type="button" data-testid={`${testId}-delete-${f.id}`} aria-label="Remove favorite" onClick={() => onDelete(f)} className="mr-2 rounded-full p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={12} /></button>}
    </div>
  );
}

/** Pick a saved medication favorite: the user's own list and the clinic-wide list. */
export function FavoritesPicker({ onPick, testId = "fav-picker" }) {
  const { user } = useAuth();
  const { open, setOpen, ref } = usePopover();
  const [rows, setRows] = useState(null);
  const load = () => api.get("/medication-favorites").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  const del = async (f) => { try { await api.delete(`/medication-favorites/${f.id}`); toast.success("Favorite removed"); load(); } catch (e) { toast.error(apiError(e)); } };
  const groups = [["My favorites", (rows || []).filter((f) => f.scope === "personal")], ["Clinic favorites", (rows || []).filter((f) => f.scope === "clinic")]];
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={testId} onClick={() => { setOpen(!open); if (!open) load(); }}
        className="flex items-center gap-1 rounded-full border border-[#E5DFD3] bg-white px-3 py-1 text-xs font-semibold text-[#4E342E] transition-colors hover:bg-[#F5F2EB]"><Star size={12} className="text-[#8D5A00]" /> Favorites <ChevronDown size={12} /></button>
      {open && (
        <div data-testid={`${testId}-menu`} className="absolute left-0 z-50 mt-1 max-h-80 w-80 overflow-y-auto rounded-xl border border-[#EFEBE0] bg-white py-1 shadow-lg">
          {!rows && <div className="px-3 py-3 text-xs text-[#84675B]">Loading favorites</div>}
          {rows && groups.map(([title, list]) => (
            <div key={title}><div className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{title}</div>
              {list.length ? list.map((f) => <FavRow key={f.id} f={f} testId={testId} onDelete={del} onPick={(x) => { setOpen(false); onPick(x); }}
                canDelete={f.owner_id === user?.id || (f.scope === "clinic" && user?.role === "admin")} />)
                : <div className="px-3 pb-2 text-xs text-[#84675B]">None saved yet.</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Save the current medication setup as a personal or clinic-wide favorite. */
export function SaveFavoriteButton({ value, testId = "fav-save" }) {
  const { user } = useAuth();
  const { open, setOpen, ref } = usePopover();
  const canClinic = user?.role === "admin" || user?.is_prescriber;
  const save = async (scope) => {
    const body = { scope, daw: value.daw === true || value.daw === "Yes", prn: !!value.prn };
    FAV_KEYS.forEach((k) => { body[k] = value[k] || ""; });
    try { await api.post("/medication-favorites", body); toast.success(scope === "clinic" ? "Saved to clinic favorites" : "Saved to your favorites"); setOpen(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!value.medication) return null;
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={testId} onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs font-semibold text-[#8D5A00] hover:underline"><Star size={12} /> Save as favorite</button>
      {open && (
        <div data-testid={`${testId}-menu`} className="absolute right-0 z-50 mt-1 w-56 rounded-xl border border-[#EFEBE0] bg-white p-1 shadow-lg">
          <button type="button" data-testid={`${testId}-personal`} onClick={() => save("personal")} className="w-full rounded-lg px-3 py-2 text-left text-sm text-[#3E2723] hover:bg-[#F5F2EB]">Save to my favorites</button>
          {canClinic && <button type="button" data-testid={`${testId}-clinic`} onClick={() => save("clinic")} className="w-full rounded-lg px-3 py-2 text-left text-sm text-[#3E2723] hover:bg-[#F5F2EB]">Save to clinic favorites</button>}
        </div>
      )}
    </div>
  );
}
